import React, { useEffect, useState } from 'react';
import {
  Box, Button, Alert, Typography, Paper, Grid, Divider,
} from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PrintIcon from '@mui/icons-material/Print';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { superAdminAPI } from '../../services/api';
import { PageHeader } from '../../components/ui/PageHeader';
import { apiErrorMessage, statusChip } from '../../components/superadmin/SuperAdminDataTable';
import {
  PlatformTransactionReceiptPrint,
  printPlatformTransactionReceipt,
  downloadPlatformTransactionReceiptPdf,
} from '../../components/superadmin/PlatformTransactionReceiptPrint';
import { useLocale } from '../../contexts/LocaleContext';
import { RADIUS, brandColors } from '../../theme/appTheme';
import { catchyPrimaryButtonSx } from '../../theme/formStyles';

const isMongoId = (id: string) => /^[a-f\d]{24}$/i.test(id);

export const TransactionDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [transaction, setTransaction] = useState<any>(location.state?.transaction || null);
  const [platform, setPlatform] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);
  const { formatCurrency, formatDate } = useLocale();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [profileRes, txnRes] = await Promise.all([
          superAdminAPI.getBillingProfile(),
          id && isMongoId(id) ? superAdminAPI.getTransaction(id) : Promise.resolve(null),
        ]);
        setPlatform(profileRes.data);
        if (txnRes?.data?.transaction) {
          setTransaction(txnRes.data.transaction);
        } else if (!transaction && id) {
          setError('Transaction not found. Open it from the transactions list.');
        }
      } catch (err) {
        if (!transaction) {
          setError(apiErrorMessage(err, 'Failed to load transaction'));
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const handleDownloadPdf = async () => {
    const receiptNo = transaction?.reference || transaction?._id;
    if (!receiptNo) return;
    setPdfLoading(true);
    try {
      await downloadPlatformTransactionReceiptPdf(String(receiptNo));
    } catch {
      setError('PDF download failed. Try Print instead.');
    } finally {
      setPdfLoading(false);
    }
  };

  if (loading) {
    return <Box sx={{ p: 3 }}><Typography>Loading receipt...</Typography></Box>;
  }

  if (!transaction) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error || 'Transaction not found'}</Alert>
        <Button onClick={() => navigate('/super-admin/transactions')}>Back to Transactions</Button>
      </Box>
    );
  }

  const receiptNo = transaction.reference || `RCP-${String(transaction._id || '').slice(-8).toUpperCase()}`;
  const paidOn = transaction.paidAt || transaction.date || transaction.createdAt;

  return (
    <Box>
      <PageHeader
        title={receiptNo}
        subtitle={`Payment receipt · ${transaction.companyName || transaction.tenant} · ${transaction.planName || transaction.plan}`}
        icon={<ReceiptLongIcon />}

        action={
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button variant="outlined" startIcon={<PictureAsPdfIcon />}
              onClick={handleDownloadPdf} disabled={pdfLoading}
              sx={{ textTransform: 'none', fontWeight: 600 }}>
              {pdfLoading ? 'Generating…' : 'Download PDF'}
            </Button>
            <Button variant="contained" startIcon={<PrintIcon />}
              onClick={printPlatformTransactionReceipt} sx={catchyPrimaryButtonSx}>
              Print Receipt
            </Button>
          </Box>
        }
      />
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 0, borderRadius: `${RADIUS}px`, border: `1px solid ${brandColors.border}`, overflow: 'hidden' }}>
            <PlatformTransactionReceiptPrint transaction={transaction} platform={platform} />
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: `${RADIUS}px`, border: `1px solid ${brandColors.border}` }}>
            <Typography fontWeight={700} mb={2}>Transaction Summary</Typography>
            <Typography fontSize={13} color="text.secondary">Receipt No</Typography>
            <Typography fontFamily="monospace" fontWeight={800} mb={1.5}>{receiptNo}</Typography>
            <Typography fontSize={13} color="text.secondary">Client</Typography>
            <Typography fontWeight={600} mb={0.5}>{transaction.companyName || transaction.tenant}</Typography>
            {transaction.tenantEmail && (
              <Typography fontSize={12} color="text.secondary" mb={0.5}>{transaction.tenantEmail}</Typography>
            )}
            {transaction.tenantMobile && (
              <Typography fontSize={12} color="text.secondary" mb={1.5}>{transaction.tenantMobile}</Typography>
            )}
            {!transaction.tenantMobile && <Box mb={1.5} />}
            {transaction.tenantGst && (
              <>
                <Typography fontSize={13} color="text.secondary">GSTIN</Typography>
                <Typography fontWeight={600} mb={1.5}>{transaction.tenantGst}</Typography>
              </>
            )}
            {transaction.tenantId && (
              <>
                <Typography fontSize={13} color="text.secondary">Tenant ID</Typography>
                <Typography fontFamily="monospace" fontSize={12} mb={1.5}>{transaction.tenantId}</Typography>
              </>
            )}
            {(transaction.tenantCity || transaction.tenantState) && (
              <>
                <Typography fontSize={13} color="text.secondary">Location</Typography>
                <Typography fontWeight={600} mb={1.5}>
                  {[transaction.tenantCity, transaction.tenantState, transaction.tenantCountry].filter(Boolean).join(', ')}
                </Typography>
              </>
            )}
            <Typography fontSize={13} color="text.secondary">Plan</Typography>
            <Typography fontWeight={600} mb={1.5}>{transaction.planName || transaction.plan}</Typography>
            <Typography fontSize={13} color="text.secondary">Amount</Typography>
            <Typography fontWeight={800} fontSize={20} color="primary" mb={0.5}>
              {formatCurrency(transaction.amount)}
            </Typography>
            <Typography fontSize={12} color="text.secondary" mb={1.5}>{transaction.currency || 'INR'}</Typography>
            <Typography fontSize={13} color="text.secondary">Status</Typography>
            <Box mb={1.5}>{statusChip(transaction.status)}</Box>
            <Typography fontSize={13} color="text.secondary">Payment Date</Typography>
            <Typography fontWeight={600} mb={2}>{paidOn ? formatDate(paidOn) : '—'}</Typography>
            <Divider sx={{ my: 2 }} />
            <Typography fontSize={12} color="text.secondary">
              Download PDF or print this receipt to share payment confirmation with your client.
            </Typography>
            <Button fullWidth sx={{ mt: 2 }} onClick={() => navigate('/super-admin/transactions')}>
              Back to Transactions
            </Button>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};
